/**
 * Debt & Financing Section
 * Extracted from FinancePage.jsx for better maintainability
 * Design: Quiet Luxury Corporativo - Angolan Night theme
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, Building2, Percent, Activity, Wallet,
  ArrowUpRight, ArrowDownRight, Minus, Landmark, Shield, FileText, AlertCircle,
  Info, Target, BarChart3, CreditCard
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import ExecutiveDrilldownCard from '../../components/ExecutiveDrilldownCard';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

// Utility function
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip - Standard Format (Balanço Patrimonial style)
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// ============ SEMANTIC COLOR SYSTEM - SCORECARD PATTERN ============
const statusColors = {
  positive: '#0C8A4B',
  positiveBg: '#EAF7EF',
  warning: '#A07A18',
  warningBg: '#F8F1DD',
  negative: '#C33D52',
  negativeBg: '#FBECEF',
};

const getSemanticBadgeStyle = (val, inverse = false, useWarning = true) => {
  if (val === undefined || val === null) return { bg: 'bg-slate-100', text: 'text-slate-600' };
  const isFavorable = inverse ? val <= 0 : val >= 0;
  const absVal = Math.abs(val);
  if (useWarning && !isFavorable && absVal <= 5) {
    return { bg: 'bg-amber-50', text: 'text-amber-700', bgColor: statusColors.warningBg, color: statusColors.warning };
  }
  if (isFavorable) {
    return { bg: 'bg-emerald-100', text: 'text-emerald-700', bgColor: statusColors.positiveBg, color: statusColors.positive };
  }
  return { bg: 'bg-rose-100', text: 'text-rose-700', bgColor: statusColors.negativeBg, color: statusColors.negative };
};

const getTrendIconInfo = (vsBudget, vsYoY, inverse = false) => {
  const mainVal = vsBudget !== undefined ? vsBudget : vsYoY;
  if (mainVal === undefined) return null;
  const isFavorable = inverse ? mainVal <= 0 : mainVal >= 0;
  const isUp = mainVal > 0;
  return {
    isUp,
    bgColor: isFavorable ? statusColors.positiveBg : statusColors.negativeBg,
    iconColor: isFavorable ? statusColors.positive : statusColors.negative,
  };
};

// KPI Card with Tooltip - SCORECARD EXECUTIVO PATTERN
const KPICardWithTooltip = ({ 
  title, value, unit, vsBudget, vsYoY, changeUnit = '%',
  tooltipData, 
  bgClass = '', language, inverse = false, useWarning = true 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Get semantic styles
  const budgetStyle = getSemanticBadgeStyle(vsBudget, inverse, useWarning);
  const yoyStyle = getSemanticBadgeStyle(vsYoY, inverse, useWarning);
  const trendInfo = getTrendIconInfo(vsBudget, vsYoY, inverse);

  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg hover:border-gold-300/50 transition-all duration-300 overflow-visible relative ${bgClass}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      {/* SCORECARD LAYOUT: Content on left, Trend Icon on right */}
      <div className="flex items-start justify-between">
        {/* Left side: Title, Value, Badges */}
        <div className="flex-1 min-w-0">
          {/* Title - Normal case, not uppercase */}
          <p className="text-xs text-slate-500 tracking-wide mb-1.5">{title}</p>
          
          {/* Value + Unit - Smaller size to match Personnel Costs */}
          <div className="flex items-baseline gap-1.5 mb-2">
            <span className="font-sans text-2xl font-semibold tracking-[-0.3px] text-primary">
              {value}
            </span>
            <span className="text-sm text-slate-400">{unit}</span>
          </div>
          
          {/* Comparison Badges - Inline compact style with no wrap */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {vsBudget !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${budgetStyle.bg} ${budgetStyle.text}`}>
                {vsBudget >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsBudget > 0 ? '+' : ''}{vsBudget}{changeUnit} vs Orç.
              </span>
            )}
            {vsYoY !== undefined && (
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap ${yoyStyle.bg} ${yoyStyle.text}`}>
                {vsYoY >= 0 ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                {vsYoY > 0 ? '+' : ''}{vsYoY}{changeUnit} YoY
              </span>
            )}
          </div>
        </div>
        
        {/* Right side: Circular Trend Icon - SCORECARD PATTERN */}
        {trendInfo && (
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
            style={{ backgroundColor: trendInfo.bgColor }}
          >
            {trendInfo.isUp ? (
              <ArrowUpRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            ) : (
              <ArrowDownRight className="w-5 h-5" style={{ color: trendInfo.iconColor }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const DebtFinancing = ({ year, language, setActiveSection, flowMap }) => {
  const yearlyData = mockData.finance.yearly[year];
  const prevYearData = mockData.finance.yearly[year - 1] || mockData.finance.yearly[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';
  
  // Budget estimates for debt metrics
  const budgetGrossDebt = yearlyData.totalDebt * 1.05; // Budget was 5% higher
  const budgetNetDebt = yearlyData.netDebt * 1.12; // Budget was 12% higher
  const budgetWACD = 9.0; // Budget target
  const budgetDebtServiceCoverage = 6.5; // Budget target
  const budgetCurrentRatio = 1.35; // Budget target
  
  // Calculate vs Budget
  const grossDebtVsBudget = ((yearlyData.totalDebt - budgetGrossDebt) / budgetGrossDebt * 100).toFixed(0);
  const netDebtVsBudget = ((yearlyData.netDebt - budgetNetDebt) / budgetNetDebt * 100).toFixed(0);
  const wacdVsBudget = (8.5 - budgetWACD).toFixed(1); // In p.p.
  
  // YoY calculations
  const grossDebtYoY = ((yearlyData.totalDebt / (prevYearData?.totalDebt || 105) - 1) * 100).toFixed(0);
  const netDebtYoY = ((yearlyData.netDebt / (prevYearData?.netDebt || 44) - 1) * 100).toFixed(0);
  const wacdYoY = -0.8; // From 9.3% to 8.5%
  
  // Insights
  const debtInsights = {
    pt: [
      { title: 'CUSTO E QUALIDADE DO FUNDING', description: 'A amortização acelerada da linha em AOA do Banco Valor e a migração para instrumentos mais competitivos suportam melhoria do custo médio e reduzem a vulnerabilidade financeira.', color: 'text-emerald-400' },
      { title: 'DIVERSIFICAÇÃO BANCÁRIA', description: 'O acesso simultâneo a cinco financiadores reforça credibilidade, reduz concentração e melhora o poder negocial da ABC.', color: 'text-gold-400' },
      { title: 'FUNDING DO CRESCIMENTO', description: 'O financiamento está desenhado para suportar a nova lavaria, reforço de frota, liquidez e execução do plano de produção sem recorrer a crédito de curto prazo mais oneroso.', color: 'text-blue-400' },
    ],
    en: [
      { title: 'COST & FUNDING QUALITY', description: 'The accelerated amortization of the AOA line from Banco Valor and migration to more competitive instruments support average cost improvement and reduce financial vulnerability.', color: 'text-emerald-400' },
      { title: 'BANKING DIVERSIFICATION', description: 'Simultaneous access to five lenders reinforces credibility, reduces concentration and improves ABC\'s negotiating power.', color: 'text-gold-400' },
      { title: 'GROWTH FUNDING', description: 'Financing is designed to support the new washing plant, fleet reinforcement, liquidity and production plan execution without resorting to more expensive short-term credit.', color: 'text-blue-400' },
    ],
  };

  // Calculate metrics
  const debtServiceCoverage = (yearlyData.ebitda / (yearlyData.totalDebt * 0.15)).toFixed(1);
  const prevDebtServiceCoverage = 6.2; // Previous year
  const debtServiceVsBudget = ((parseFloat(debtServiceCoverage) - budgetDebtServiceCoverage) / budgetDebtServiceCoverage * 100).toFixed(0);
  const debtServiceYoY = (parseFloat(debtServiceCoverage) - prevDebtServiceCoverage).toFixed(1);
  
  const currentRatio = (mockData.balanceSheet[year]?.currentAssets / (mockData.balanceSheet[year]?.shortTermDebt + mockData.balanceSheet[year]?.suppliers + mockData.balanceSheet[year]?.otherLiabilities) || 1.44).toFixed(2);
  const prevCurrentRatio = 1.29; // Previous year
  const currentRatioVsBudget = ((parseFloat(currentRatio) - budgetCurrentRatio) / budgetCurrentRatio * 100).toFixed(0);
  const currentRatioYoY = (parseFloat(currentRatio) - prevCurrentRatio).toFixed(2);

  // Financing plan waterfall data - Sources add, Uses subtract, ending with balance
  const financingWaterfallData = [
    { name: 'BCS', value: 46, isSource: true, runningTotal: 46 },
    { name: 'BAI', value: 23, isSource: true, runningTotal: 69 },
    { name: 'BCGA', value: 14, isSource: true, runningTotal: 83 },
    { name: 'B. Valor', value: 9, isSource: true, runningTotal: 92 },
    { name: 'CAPEX', value: -45, isSource: false, runningTotal: 47 },
    { name: language === 'pt' ? 'Serv. Dívida' : 'Debt Serv.', value: -26, isSource: false, runningTotal: 21 },
    { name: language === 'pt' ? 'Fundo Maneio' : 'Work. Cap.', value: -15, isSource: false, runningTotal: 6 },
    { name: language === 'pt' ? 'Folga' : 'Buffer', value: 6, isTotal: true, runningTotal: 6 },
  ];
  
  // Calculate waterfall bar positions
  const waterfallBars = financingWaterfallData.map((item, idx) => {
    if (idx === 0) {
      return { ...item, start: 0, end: item.value };
    }
    if (item.isTotal) {
      return { ...item, start: 0, end: item.value };
    }
    const prevTotal = financingWaterfallData[idx - 1].runningTotal;
    if (item.value >= 0) {
      return { ...item, start: prevTotal, end: prevTotal + item.value };
    } else {
      return { ...item, start: prevTotal + item.value, end: prevTotal };
    }
  });

  // Debt mix data - 2025 vs 2024 with CP/MLP split for 2 banks
  const debtMixData = [
    { 
      bank: 'BCS', 
      value2025: 46.0, pct2025: 50, 
      value2024: 52.0, pct2024: 52,
      rate: '7.5%', 
      // BCS has both MLP and CP
      mlp2025: 40.0, cp2025: 6.0,
      mlp2024: 45.0, cp2024: 7.0,
      hasSplit: true
    },
    { 
      bank: 'BAI', 
      value2025: 23.0, pct2025: 25, 
      value2024: 26.0, pct2024: 26,
      rate: '9%', 
      // BAI has both MLP and CP
      mlp2025: 18.0, cp2025: 5.0,
      mlp2024: 20.0, cp2024: 6.0,
      hasSplit: true
    },
    { 
      bank: 'BCGA', 
      value2025: 13.8, pct2025: 15, 
      value2024: 12.0, pct2024: 12,
      rate: '8.5%', 
      type: 'MLP',
      hasSplit: false
    },
    { 
      bank: 'Banco Valor', 
      value2025: 9.2, pct2025: 10, 
      value2024: 10.0, pct2024: 10,
      rate: '12%', 
      type: 'CP',
      hasSplit: false
    },
  ];
  
  // Calculate totals for debt mix
  const totalDebt2025 = debtMixData.reduce((sum, d) => sum + d.value2025, 0);
  const totalDebt2024 = debtMixData.reduce((sum, d) => sum + d.value2024, 0);
  const debtMixVariation = ((totalDebt2025 - totalDebt2024) / totalDebt2024 * 100).toFixed(1);

  // Debt service data - 2025 vs 2024 comparison
  const debtServiceData = [
    { bank: 'BCS', amortization2025: 8.5, interest2025: 3.5, amortization2024: 7.8, interest2024: 4.0 },
    { bank: 'BAI', amortization2025: 4.2, interest2025: 2.1, amortization2024: 4.5, interest2024: 2.5 },
    { bank: 'BCGA', amortization2025: 3.2, interest2025: 1.2, amortization2024: 2.8, interest2024: 1.4 },
    { bank: 'B. Valor', amortization2025: 2.6, interest2025: 1.1, amortization2024: 3.0, interest2024: 1.5 },
  ];
  
  // Calculate totals
  const totalAmortization2025 = debtServiceData.reduce((sum, d) => sum + d.amortization2025, 0);
  const totalInterest2025 = debtServiceData.reduce((sum, d) => sum + d.interest2025, 0);
  const totalService2025 = totalAmortization2025 + totalInterest2025;
  const totalAmortization2024 = debtServiceData.reduce((sum, d) => sum + d.amortization2024, 0);
  const totalInterest2024 = debtServiceData.reduce((sum, d) => sum + d.interest2024, 0);
  const totalService2024 = totalAmortization2024 + totalInterest2024;
  const serviceVariation = ((totalService2025 - totalService2024) / totalService2024 * 100).toFixed(1);

  const bankColors = {
    'BCS': '#0A192F',
    'BAI': '#1E40AF',
    'BCGA': '#C5A059',
    'Banco Valor': '#3B82F6',
    'B. Valor': '#3B82F6',
  };

  return (
    <div className="space-y-8" data-testid="debt-financing-section">
      {/* Page Title */}
      <div>
        <h1 
          className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
          style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
        >
          {language === 'pt' ? 'Dívida e Financiamento' : 'Debt & Financing'} | {titleSuffix}
        </h1>
        <p 
          className="text-[15px] leading-[22px]"
          style={{ color: '#425070' }}
        >
          {comparisonSubtitle}
        </p>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {debtInsights[language].map((insight, idx) => (
            <div 
              key={idx} 
              className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  idx === 0 ? 'bg-emerald-500/30' : idx === 1 ? 'bg-amber-500/30' : 'bg-blue-500/30'
                }`}>
                  {idx === 0 ? (
                    <TrendingDown className="w-5 h-5 text-emerald-400" />
                  ) : idx === 1 ? (
                    <Building2 className="w-5 h-5 text-amber-400" />
                  ) : (
                    <TrendingUp className="w-5 h-5 text-blue-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-5 gap-4 overflow-visible">
        <KPICardWithTooltip
          title={language === 'pt' ? 'Dívida Bruta' : 'Gross Debt'}
          value={formatM(yearlyData.totalDebt)}
          unit="M USD"
          vsBudget={parseInt(grossDebtVsBudget)}
          vsYoY={parseInt(grossDebtYoY)}
          inverse={true}
          tooltipData={{
            title: language === 'pt' ? 'Dívida Bruta' : 'Gross Debt',
            definition: language === 'pt' 
              ? 'Montante total de financiamento remunerado em balanço.'
              : 'Total amount of interest-bearing financing on balance sheet.',
            relevance: language === 'pt'
              ? 'Indica o nível de alavancagem financeira da empresa.'
              : 'Indicates the company\'s financial leverage level.',
            currentReading: language === 'pt'
              ? 'A dívida bruta de USD 92M está 5% abaixo do orçamento, refletindo amortizações aceleradas e gestão prudente do passivo financeiro. O stock de dívida é confortável face à geração de caixa operacional.'
              : 'Gross debt of USD 92M is 5% below budget, reflecting accelerated amortizations and prudent financial liability management. Debt stock is comfortable relative to operating cash generation.'
          }}
          language={language}
        />
        <KPICardWithTooltip
          title={language === 'pt' ? 'Dívida Líquida' : 'Net Debt'}
          value={formatM(yearlyData.netDebt)}
          unit="M USD"
          vsBudget={parseInt(netDebtVsBudget)}
          vsYoY={parseInt(netDebtYoY)}
          inverse={true}
          tooltipData={{
            title: language === 'pt' ? 'Dívida Líquida' : 'Net Debt',
            definition: language === 'pt' 
              ? 'Dívida financeira após dedução da caixa disponível.'
              : 'Financial debt after deducting available cash.',
            relevance: language === 'pt'
              ? 'Métrica real de endividamento, descontada da liquidez.'
              : 'True indebtedness metric, adjusted for liquidity.',
            currentReading: language === 'pt'
              ? 'Dívida líquida de USD 36M representa redução de 33% YoY, beneficiando da forte geração de caixa operacional. O rácio Dívida Líquida/EBITDA situa-se em 0.35x, nível de baixo risco financeiro.'
              : 'Net debt of USD 36M represents 33% YoY reduction, benefiting from strong operating cash generation. Net Debt/EBITDA ratio stands at 0.35x, a low financial risk level.'
          }}
          language={language}
        />
        <KPICardWithTooltip
          title="WACD"
          value="8.5"
          unit="%"
          vsBudget={parseFloat(wacdVsBudget)}
          vsYoY={wacdYoY}
          changeUnit="p.p."
          inverse={true}
          bgClass="border-emerald-200 bg-emerald-50/30"
          tooltipData={{
            title: 'WACD',
            definition: language === 'pt' 
              ? 'Custo médio ponderado de toda a dívida em balanço.'
              : 'Weighted average cost of all debt on balance sheet.',
            relevance: language === 'pt'
              ? 'Mede a eficiência na captação de financiamento.'
              : 'Measures efficiency in obtaining financing.',
            currentReading: language === 'pt'
              ? 'WACD de 8.5% representa melhoria de 0.5p.p. vs orçamento e 0.8p.p. vs 2024. A redução resulta da renegociação com o BCS e amortização de linhas mais caras (Banco Valor a 12%).'
              : 'WACD of 8.5% represents 0.5p.p. improvement vs budget and 0.8p.p. vs 2024. Reduction results from BCS renegotiation and amortization of more expensive lines (Banco Valor at 12%).'
          }}
          language={language}
        />
        <KPICardWithTooltip
          title={language === 'pt' ? 'EBITDA / Serv. Dívida' : 'EBITDA / Debt Service'}
          value={debtServiceCoverage}
          unit="x"
          vsBudget={parseInt(debtServiceVsBudget)}
          vsYoY={parseFloat(debtServiceYoY)}
          changeUnit="x"
          tooltipData={{
            title: language === 'pt' ? 'EBITDA / Serviço da Dívida' : 'EBITDA / Debt Service',
            definition: language === 'pt' 
              ? 'Capacidade do EBITDA cobrir o serviço anual da dívida.'
              : 'EBITDA\'s ability to cover annual debt service.',
            relevance: language === 'pt'
              ? 'Indica conforto financeiro para cumprir obrigações de dívida.'
              : 'Indicates financial comfort for meeting debt obligations.',
            currentReading: language === 'pt'
              ? 'Cobertura de 7.4x está significativamente acima do covenant mínimo de 2.5x. O EBITDA robusto garante folga confortável para serviço da dívida mesmo em cenários adversos de produção ou preço.'
              : 'Coverage of 7.4x is significantly above the minimum covenant of 2.5x. Robust EBITDA ensures comfortable headroom for debt service even in adverse production or price scenarios.'
          }}
          language={language}
        />
        <KPICardWithTooltip
          title={language === 'pt' ? 'Liquidez Corrente' : 'Current Ratio'}
          value={currentRatio}
          unit="x"
          vsBudget={parseInt(currentRatioVsBudget)}
          vsYoY={parseFloat(currentRatioYoY)}
          changeUnit="x"
          tooltipData={{
            title: language === 'pt' ? 'Liquidez Corrente' : 'Current Ratio',
            definition: language === 'pt' 
              ? 'Rácio entre activos correntes e passivos correntes.'
              : 'Ratio between current assets and current liabilities.',
            relevance: language === 'pt'
              ? 'Mede a capacidade de honrar obrigações de curto prazo.'
              : 'Measures ability to meet short-term obligations.',
            currentReading: language === 'pt'
              ? 'Rácio de 1.44x indica posição de liquidez saudável. Os activos correntes cobrem confortavelmente as obrigações de curto prazo, sem stress de tesouraria previsível nos próximos 12 meses.'
              : 'Ratio of 1.44x indicates healthy liquidity position. Current assets comfortably cover short-term obligations, with no foreseeable treasury stress in the next 12 months.'
          }}
          language={language}
        />
      </div>

      {/* Executive Summary - Modular Layout Premium */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Resumo Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Dívida e financiamento' : 'Debt and financing'}
        language={language}
        maxHeight="550px"
        icon={Info}
        dataTestId="debt-financing-executive-summary"
      >
        <div className="space-y-6">
          {/* 1. Visão do Ano */}
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <h4 className="font-semibold text-sm text-slate-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-slate-600"></div>
              {language === 'pt' ? 'Visão do Ano' : 'Year Overview'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, a secção de <strong>Dívida e Financiamento</strong> mostra uma melhoria simultânea em <strong>alavancagem, custo do funding e capacidade de cobertura</strong>, mantendo ao mesmo tempo uma base de financiamento suficiente para suportar o plano. A <strong>dívida bruta</strong> fechou em <strong>USD 92,0M</strong>, <strong>5% abaixo do orçamento</strong> e <strong>4% abaixo de 2024</strong>, enquanto a <strong>dívida líquida</strong> terminou em <strong>USD 36,0M</strong>, ficando <strong>11% abaixo do plano</strong> e <strong>33% abaixo do período homólogo</strong>. Em paralelo, o <strong>WACD desceu para 8,5%</strong>, melhorando <strong>0,5 p.p. face ao orçamento</strong> e <strong>0,8 p.p. face a 2024</strong>.</>
                  : <>In 2025, the <strong>Debt and Financing</strong> section shows simultaneous improvement in <strong>leverage, funding cost and coverage capacity</strong>, while maintaining a financing base sufficient to support the plan. <strong>Gross debt</strong> closed at <strong>USD 92.0M</strong>, <strong>5% below budget</strong> and <strong>4% below 2024</strong>, while <strong>net debt</strong> ended at <strong>USD 36.0M</strong>, <strong>11% below plan</strong> and <strong>33% below the prior year</strong>. In parallel, <strong>WACD fell to 8.5%</strong>, improving <strong>0.5 p.p. vs budget</strong> and <strong>0.8 p.p. vs 2024</strong>.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura principal para a Gerência é que o perfil financeiro do ano melhorou sem depender de compressão artificial da liquidez. A empresa terminou 2025 com <strong>menor stock líquido de dívida</strong>, <strong>menor custo médio</strong> e <strong>maior capacidade de cobertura do serviço da dívida</strong>, o que reforça a flexibilidade financeira para o ciclo seguinte.</>
                  : <>The main reading for Management is that the year's financial profile improved without depending on artificial liquidity compression. The company ended 2025 with <strong>lower net debt stock</strong>, <strong>lower average cost</strong> and <strong>greater debt service coverage capacity</strong>, reinforcing financial flexibility for the next cycle.</>}
              </p>
            </div>
          </div>

          {/* 2. Qualidade do Funding */}
          <div className="bg-emerald-50/60 rounded-xl p-5 border border-emerald-200">
            <h4 className="font-semibold text-sm text-emerald-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-emerald-600"></div>
              {language === 'pt' ? 'Qualidade do Funding' : 'Funding Quality'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>O ponto mais relevante desta secção não é apenas o nível da dívida, mas a <strong>qualidade do funding contratado</strong>. A descida do <strong>WACD para 8,5%</strong> mostra que a estrutura foi sendo deslocada para um perfil mais competitivo, mesmo num contexto de financiamento ainda material. A combinação entre amortização, melhoria de mix e menor peso relativo de linhas mais onerosas permitiu reduzir o custo médio e melhorar a leitura global do passivo financeiro.</>
                  : <>The most relevant point of this section is not just the debt level, but the <strong>quality of contracted funding</strong>. The <strong>WACD drop to 8.5%</strong> shows that the structure has been shifting to a more competitive profile, even in a still material financing context.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>Ao mesmo tempo, a página confirma que o custo continua suficientemente relevante para justificar acompanhamento activo. Dentro da dívida bancária detalhada, a <strong>taxa média ponderada é de 8,2%</strong>, com alguma dispersão entre instrumentos:</>
                  : <>At the same time, the page confirms that the cost remains sufficiently relevant to justify active monitoring. Within detailed bank debt, the <strong>weighted average rate is 8.2%</strong>, with some dispersion among instruments:</>}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li className="flex justify-between"><span>BFA – Linha MLP:</span><span className="font-semibold">7,5%</span></li>
                    <li className="flex justify-between"><span>BAI – Linha Capital:</span><span className="font-semibold">8,0%</span></li>
                    <li className="flex justify-between"><span>BPC – Conta Corrente:</span><span className="font-semibold">7,8%</span></li>
                  </ul>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                  <ul className="text-xs text-slate-600 space-y-1">
                    <li className="flex justify-between"><span>BIC – Working Capital:</span><span className="font-semibold text-amber-700">8,5%</span></li>
                    <li className="flex justify-between"><span>Standard Bank – Proj. Fin.:</span><span className="font-semibold text-rose-700">9,5%</span></li>
                  </ul>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs mt-2">
                {language === 'pt' 
                  ? 'Isto significa que, apesar da melhoria do custo médio agregado, continua a existir espaço de optimização através de reperfilamento do mix, redução de linhas mais caras e gestão mais activa de maturidades e spreads.'
                  : 'This means that, despite the improvement in aggregate average cost, there is still room for optimization through mix rebalancing, reduction of more expensive lines and more active management of maturities and spreads.'}
              </p>
            </div>
          </div>

          {/* 3. Estrutura e Diversificação da Dívida */}
          <div className="bg-blue-50/60 rounded-xl p-5 border border-blue-200">
            <h4 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-blue-600"></div>
              {language === 'pt' ? 'Estrutura e Diversificação da Dívida' : 'Debt Structure and Diversification'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A estrutura do financiamento apresenta hoje um perfil mais diversificado do que no passado. O <strong>mix de dívida</strong> está repartido entre:</>
                  : <>The financing structure now presents a more diversified profile than in the past. The <strong>debt mix</strong> is split between:</>}
              </p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <div className="bg-white/60 rounded-lg p-2.5 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BCS</p>
                  <p className="text-sm font-bold text-primary">USD 46,0M</p>
                  <p className="text-[10px] text-blue-600 font-semibold">50%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BAI</p>
                  <p className="text-sm font-bold text-primary">USD 23,0M</p>
                  <p className="text-[10px] text-blue-600 font-semibold">25%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BCGA</p>
                  <p className="text-sm font-bold text-primary">USD 13,8M</p>
                  <p className="text-[10px] text-blue-600 font-semibold">15%</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-blue-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">Banco Valor</p>
                  <p className="text-sm font-bold text-primary">USD 9,2M</p>
                  <p className="text-[10px] text-blue-600 font-semibold">10%</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Face a 2024, a exposição reduziu-se em <strong>BCS, BAI e Banco Valor</strong>, enquanto <strong>BCGA</strong> aumentou, o que sugere reequilíbrio da base de financiamento em vez de simples expansão linear da dívida. Em termos de leitura executiva, isto reduz concentração e melhora o poder negocial da empresa, mas não elimina o risco de dependência de poucos bancos âncora, sobretudo nas componentes de maior peso.</>
                  : <>Compared to 2024, exposure was reduced at <strong>BCS, BAI and Banco Valor</strong>, while <strong>BCGA</strong> increased, suggesting a rebalancing of the financing base rather than simple linear debt expansion.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'No detalhe da dívida bancária mapeada, o saldo total era de USD 45,2M no final de 2025:' : 'In the mapped bank debt detail, total balance was USD 45.2M at end of 2025:'}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div className="flex justify-between"><span className="text-slate-600">BFA – Linha MLP:</span><span className="font-semibold">USD 18,5M <span className="text-amber-600">(41%)</span></span></div>
                  <div className="flex justify-between"><span className="text-slate-600">BIC – Working Capital:</span><span className="font-semibold">USD 9,2M <span className="text-slate-500">(20%)</span></span></div>
                  <div className="flex justify-between"><span className="text-slate-600">Standard Bank – Proj. Fin.:</span><span className="font-semibold">USD 7,4M <span className="text-slate-500">(16%)</span></span></div>
                  <div className="flex justify-between"><span className="text-slate-600">BAI – Linha Capital:</span><span className="font-semibold">USD 5,8M <span className="text-slate-500">(13%)</span></span></div>
                  <div className="flex justify-between"><span className="text-slate-600">BPC – Conta Corrente:</span><span className="font-semibold">USD 4,3M <span className="text-slate-500">(10%)</span></span></div>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs">
                {language === 'pt' 
                  ? 'O principal ponto de concentração permanece, portanto, no BFA, que continua a representar o maior saldo individual.'
                  : 'The main concentration point therefore remains at BFA, which continues to represent the largest individual balance.'}
              </p>
            </div>
          </div>

          {/* 4. Funding do Plano e Folga Financeira */}
          <div className="bg-violet-50/60 rounded-xl p-5 border border-violet-200">
            <h4 className="font-semibold text-sm text-violet-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-violet-600"></div>
              {language === 'pt' ? 'Funding do Plano e Folga Financeira' : 'Plan Funding and Financial Headroom'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A secção mostra que o plano de financiamento de 2025 foi construído para suportar simultaneamente <strong>investimento, serviço da dívida e necessidades operacionais</strong>. As <strong>fontes totais</strong> ascenderam a <strong>USD 92,0M</strong>, enquanto os <strong>usos</strong> totalizaram <strong>USD 86,0M</strong>, deixando uma <strong>folga financeira de USD 6,0M</strong>.</>
                  : <>The section shows that the 2025 financing plan was built to simultaneously support <strong>investment, debt service and operational needs</strong>. <strong>Total sources</strong> amounted to <strong>USD 92.0M</strong>, while <strong>uses</strong> totaled <strong>USD 86.0M</strong>, leaving <strong>financial headroom of USD 6.0M</strong>.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A composição dos usos é relevante:' : 'The composition of uses is relevant:'}
              </p>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-violet-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">CAPEX</p>
                  <p className="text-lg font-bold text-violet-700">USD 45,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-violet-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Serviço da Dívida' : 'Debt Service'}</p>
                  <p className="text-lg font-bold text-violet-700">USD 26,0M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-violet-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Fundo de Maneio' : 'Working Capital'}</p>
                  <p className="text-lg font-bold text-violet-700">USD 15,0M</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Ou seja, o financiamento não foi usado apenas para refinanciar dívida existente; foi também mobilizado para sustentar crescimento e liquidez operacional. A implicação para a Gerência é que a estrutura de funding do ano foi desenhada com uma função dupla:</>
                  : <>That is, financing was not used only to refinance existing debt; it was also mobilized to sustain growth and operational liquidity. The implication for Management is that the year's funding structure was designed with a dual function:</>}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'proteger a execução operacional e o investimento prioritário;' : 'protect operational execution and priority investment;'}</li>
                <li>• {language === 'pt' ? 'evitar pressão excessiva sobre liquidez de curto prazo.' : 'avoid excessive pressure on short-term liquidity.'}</li>
              </ul>
              <div className="bg-white/60 rounded-lg p-3 border border-violet-100 mt-2">
                <p className="text-xs text-violet-700 font-medium">
                  <span className="text-violet-500">▸ </span>
                  {language === 'pt' 
                    ? 'A existência de folga positiva de USD 6,0M reforça esta leitura, embora o valor não seja suficientemente elevado para justificar complacência na gestão de maturidades.'
                    : 'The existence of positive headroom of USD 6.0M reinforces this reading, although the value is not high enough to justify complacency in maturity management.'}
                </p>
              </div>
            </div>
          </div>

          {/* 5. Serviço da Dívida e Capacidade de Cobertura */}
          <div className="bg-teal-50/60 rounded-xl p-5 border border-teal-200">
            <h4 className="font-semibold text-sm text-teal-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-teal-600"></div>
              {language === 'pt' ? 'Serviço da Dívida e Capacidade de Cobertura' : 'Debt Service and Coverage Capacity'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A capacidade de cobertura melhorou materialmente. O rácio <strong>EBITDA / Serviço da Dívida</strong> fechou em <strong>7,4x</strong>, acima do orçamento e acima de 2024, o que indica uma margem de absorção mais confortável da carga financeira anual. Ao mesmo tempo, o <strong>serviço total da dívida em 2025 foi de USD 26,4M</strong>, composto por:</>
                  : <>Coverage capacity improved materially. The <strong>EBITDA / Debt Service</strong> ratio closed at <strong>7.4x</strong>, above budget and above 2024, indicating a more comfortable absorption margin for annual financial charges. At the same time, <strong>total debt service in 2025 was USD 26.4M</strong>, composed of:</>}
              </p>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="bg-white/60 rounded-lg p-3 border border-teal-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Amortizações' : 'Amortizations'}</p>
                  <p className="text-lg font-bold text-teal-700">USD 18,5M</p>
                </div>
                <div className="bg-white/60 rounded-lg p-3 border border-teal-100 text-center">
                  <p className="text-xs text-slate-500 mb-1">{language === 'pt' ? 'Juros' : 'Interest'}</p>
                  <p className="text-lg font-bold text-teal-700">USD 7,9M</p>
                </div>
              </div>
              <p>
                {language === 'pt' 
                  ? <>Este valor ficou <strong>4% abaixo de 2024</strong> (<strong>USD 27,5M</strong>), o que significa que a melhoria de cobertura não resultou apenas de maior EBITDA, mas também de uma pressão anual de serviço ligeiramente menor.</>
                  : <>This value was <strong>4% below 2024</strong> (<strong>USD 27.5M</strong>), meaning the coverage improvement did not result only from higher EBITDA, but also from slightly lower annual service pressure.</>}
              </p>
              <div className="bg-white/60 rounded-lg p-3 border border-teal-100 mt-2">
                <p className="text-xs text-teal-700 font-medium">
                  <span className="text-teal-500">▸ </span>
                  {language === 'pt' 
                    ? 'Para a Gerência, esta é uma das mensagens mais importantes da página: a empresa entrou num ponto em que a geração operacional cobre a dívida com ampla margem, o que reduz vulnerabilidade financeira e aumenta flexibilidade para financiar investimento sem recorrer de forma excessiva a crédito caro de curto prazo.'
                    : 'For Management, this is one of the most important messages on the page: the company has reached a point where operational generation covers debt with ample margin, reducing financial vulnerability and increasing flexibility to finance investment without excessive recourse to expensive short-term credit.'}
                </p>
              </div>
            </div>
          </div>

          {/* 6. Maturidades e Risco de Refinanciamento */}
          <div className="bg-rose-50/60 rounded-xl p-5 border border-rose-200">
            <h4 className="font-semibold text-sm text-rose-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-rose-600"></div>
              {language === 'pt' ? 'Maturidades e Risco de Refinanciamento' : 'Maturities and Refinancing Risk'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Apesar da melhoria global do perfil financeiro, a secção mostra um ponto objectivo de atenção: a <strong>próxima maturidade relevante é em Março de 2026</strong>, associada à <strong>linha BAI</strong>, no montante de <strong>USD 5,8M</strong>. Este tema não representa, à data, um risco estrutural para a empresa, mas merece planeamento atempado porque concentra a próxima decisão de refinanciamento / liquidação.</>
                  : <>Despite the overall improvement in the financial profile, the section shows an objective attention point: the <strong>next relevant maturity is in March 2026</strong>, associated with the <strong>BAI line</strong>, in the amount of <strong>USD 5.8M</strong>. This issue does not currently represent a structural risk for the company, but deserves timely planning as it concentrates the next refinancing / liquidation decision.</>}
              </p>
              <p>
                {language === 'pt' 
                  ? <>A leitura correcta não é tratar esta maturidade como problema isolado, mas como o principal evento financeiro de curto prazo a gerir dentro de uma estrutura que, no restante, tem perfil de vencimento mais alongado:</>
                  : <>The correct reading is not to treat this maturity as an isolated problem, but as the main short-term financial event to manage within a structure that otherwise has a longer maturity profile:</>}
              </p>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <div className="bg-white/60 rounded-lg p-2.5 border border-rose-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BFA</p>
                  <p className="text-sm font-bold text-slate-700">Dez 2028</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-rose-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BIC</p>
                  <p className="text-sm font-bold text-slate-700">Jun 2027</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-rose-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">Standard Bank</p>
                  <p className="text-sm font-bold text-slate-700">Set 2027</p>
                </div>
                <div className="bg-white/60 rounded-lg p-2.5 border border-rose-100 text-center">
                  <p className="text-xs text-slate-500 mb-0.5">BPC</p>
                  <p className="text-sm font-bold text-slate-700">Dez 2026</p>
                </div>
              </div>
              <p className="text-slate-600 italic text-xs mt-2">
                {language === 'pt' 
                  ? 'Isto significa que o risco de refinanciamento imediato está relativamente contido, mas não é inexistente.'
                  : 'This means that immediate refinancing risk is relatively contained, but not non-existent.'}
              </p>
            </div>
          </div>

          {/* 7. Liquidez e Implicação Financeira */}
          <div className="bg-cyan-50/60 rounded-xl p-5 border border-cyan-200">
            <h4 className="font-semibold text-sm text-cyan-800 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <div className="w-2 h-2 rounded-full bg-cyan-600"></div>
              {language === 'pt' ? 'Liquidez e Implicação Financeira' : 'Liquidity and Financial Implication'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>A <strong>liquidez corrente de 1,44x</strong> terminou acima do orçamento e acima do período homólogo, confirmando que a melhoria da estrutura de dívida não ocorreu à custa de compressão excessiva da posição corrente. Em termos práticos, a empresa melhorou o perfil de funding e reduziu dívida líquida mantendo uma base de liquidez compatível com os compromissos de curto prazo.</>
                  : <>The <strong>current ratio of 1.44x</strong> ended above budget and above the prior year, confirming that the improvement in debt structure did not come at the expense of excessive compression of the current position. In practical terms, the company improved its funding profile and reduced net debt while maintaining a liquidity base compatible with short-term commitments.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A combinação entre:' : 'The combination of:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'dívida líquida em queda,' : 'declining net debt,'}</li>
                <li>• {language === 'pt' ? 'WACD mais baixo,' : 'lower WACD,'}</li>
                <li>• {language === 'pt' ? 'maior cobertura do serviço da dívida,' : 'higher debt service coverage,'}</li>
                <li>• {language === 'pt' ? 'e liquidez corrente acima do plano' : 'and current liquidity above plan'}</li>
              </ul>
              <p className="text-slate-600 italic text-xs mt-2">
                {language === 'pt' 
                  ? 'sugere que 2025 fechou com uma estrutura de financiamento mais defensável e mais preparada para suportar execução.'
                  : 'suggests that 2025 closed with a more defensible financing structure, better prepared to support execution.'}
              </p>
            </div>
          </div>

          {/* 8. Síntese à Gerência - Premium Gold Callout */}
          <div className="bg-gradient-to-r from-[#FBF8F0] to-[#F8F4E8] rounded-xl p-5 border border-[#B89A3C]/30">
            <h4 className="font-semibold text-sm text-[#8B7A2F] mb-3 flex items-center gap-2 uppercase tracking-wider">
              <Target className="w-4 h-4" />
              {language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}
            </h4>
            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
              <p>
                {language === 'pt' 
                  ? <>Em 2025, a página de <strong>Dívida e Financiamento</strong> confirma uma evolução positiva do perfil financeiro da empresa. A <strong>dívida líquida caiu para USD 36,0M</strong>, o <strong>custo médio desceu para 8,5%</strong>, a <strong>cobertura do serviço da dívida subiu para 7,4x</strong> e a <strong>liquidez corrente fechou em 1,44x</strong>. Em paralelo, o financiamento captado foi suficiente para suportar <strong>CAPEX, serviço da dívida e fundo de maneio</strong>, mantendo <strong>folga financeira positiva</strong>.</>
                  : <>In 2025, the <strong>Debt and Financing</strong> page confirms a positive evolution of the company's financial profile. <strong>Net debt fell to USD 36.0M</strong>, <strong>average cost dropped to 8.5%</strong>, <strong>debt service coverage rose to 7.4x</strong> and <strong>current liquidity closed at 1.44x</strong>. In parallel, funding raised was sufficient to support <strong>CAPEX, debt service and working capital</strong>, maintaining <strong>positive financial headroom</strong>.</>}
              </p>
              <p className="font-medium text-slate-800">
                {language === 'pt' ? 'A leitura de gestão deve concentrar-se em quatro ideias:' : 'The management reading should focus on four ideas:'}
              </p>
              <ul className="text-sm text-slate-700 space-y-1 ml-2">
                <li>• {language === 'pt' ? 'a empresa terminou o ano com menor alavancagem líquida;' : 'the company ended the year with lower net leverage;'}</li>
                <li>• {language === 'pt' ? 'o funding está mais diversificado e menos concentrado do que antes;' : 'funding is more diversified and less concentrated than before;'}</li>
                <li>• {language === 'pt' ? 'a capacidade de serviço da dívida melhorou de forma clara;' : 'debt service capacity improved clearly;'}</li>
                <li>• {language === 'pt' ? 'o principal ponto de atenção desloca-se agora para a gestão da maturidade BAI em Março de 2026 e para a continuação da optimização do custo do funding.' : 'the main attention point now shifts to managing the BAI maturity in March 2026 and continuing funding cost optimization.'}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ============ DRILLDOWN NAVIGATION CARDS ============ */}
        {setActiveSection && (
          <div className="mt-6 pt-4 border-t border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              {language === 'pt' ? 'Análises Relacionadas' : 'Related Analysis'}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <ExecutiveDrilldownCard
                icon={Wallet}
                title={language === 'pt' ? 'Liquidez' : 'Liquidity'}
                subtitle={language === 'pt' ? 'Caixa e cobertura' : 'Cash and coverage'}
                navigateTo="liquidity"
                setActiveSection={setActiveSection}
                color="emerald"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Analise como a liquidez suporta o serviço da dívida.'
                    : 'Analyze how liquidity supports debt service.'}
                </p>
              </ExecutiveDrilldownCard>

              <ExecutiveDrilldownCard
                icon={Landmark}
                title={language === 'pt' ? 'Resultados Financeiros' : 'Financial Results'}
                subtitle={language === 'pt' ? 'Custo do funding' : 'Funding cost'}
                navigateTo="financial-results"
                setActiveSection={setActiveSection}
                color="blue"
                language={language}
              >
                <p>
                  {language === 'pt' 
                    ? 'Veja o impacto do custo da dívida nos resultados financeiros.'
                    : 'See debt cost impact on financial results.'}
                </p>
              </ExecutiveDrilldownCard>
            </div>
          </div>
        )}
      </ExecutiveSummarySection>

      {/* Financing Plan Waterfall Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="font-serif text-lg font-semibold text-primary mb-2">
          {language === 'pt' ? 'Financiamento do Plano' : 'Plan Financing'}
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          {language === 'pt' ? `Fontes e usos de financiamento ${year} (M USD)` : `Sources and uses of financing ${year} (M USD)`}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart 
            data={waterfallBars} 
            margin={{ top: 30, right: 30, left: 20, bottom: 60 }}
            barCategoryGap="15%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8"
              angle={-35}
              textAnchor="end"
              height={70}
              interval={0}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: '#64748B' }} 
              stroke="#94A3B8" 
              domain={[0, 100]}
              tickFormatter={(v) => `${v}M`}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-[#0A192F] text-white rounded-lg px-4 py-3 shadow-xl border border-slate-700">
                      <p className="font-semibold text-gold-400 mb-2">{data.name}</p>
                      <div className="space-y-1 text-xs">
                        <p className="text-slate-300">
                          {data.isTotal 
                            ? (language === 'pt' ? 'Saldo Final' : 'Final Balance')
                            : data.value > 0 
                              ? (language === 'pt' ? 'Fonte' : 'Source')
                              : (language === 'pt' ? 'Uso' : 'Use')}:
                          <span className={`ml-2 font-bold ${data.isTotal ? 'text-blue-400' : data.value > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {data.value > 0 ? '+' : ''}{data.value}M USD
                          </span>
                        </p>
                        <p className="text-slate-400 pt-1 border-t border-slate-600">
                          {language === 'pt' ? 'Acumulado' : 'Running Total'}: 
                          <span className="font-semibold text-white ml-2">{data.runningTotal}M USD</span>
                        </p>
                      </div>
                    </div>
                  );
                }
                return null;
              }}
            />
            {/* Invisible bar for positioning */}
            <Bar dataKey="start" stackId="stack" fill="transparent" />
            {/* Visible waterfall bar */}
            <Bar 
              dataKey={(d) => Math.abs(d.end - d.start)} 
              stackId="stack" 
              radius={[4, 4, 0, 0]}
            >
              {waterfallBars.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.isTotal ? '#3B82F6' : entry.value > 0 ? '#059669' : '#DC2626'}
                />
              ))}
              <LabelList 
                dataKey="value"
                position="top"
                fill="#374151"
                fontSize={11}
                fontWeight={600}
                formatter={(value) => value > 0 ? `+${value}M` : `${value}M`}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {/* Legend */}
        <div className="flex justify-center gap-8 mt-2 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-emerald-600 rounded"></div>
            <span className="text-slate-600">{language === 'pt' ? 'Fontes (+)' : 'Sources (+)'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-red-600 rounded"></div>
            <span className="text-slate-600">{language === 'pt' ? 'Usos (-)' : 'Uses (-)'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-blue-500 rounded"></div>
            <span className="text-slate-600">{language === 'pt' ? 'Saldo' : 'Balance'}</span>
          </div>
        </div>
        {/* Summary row */}
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs text-slate-500">{language === 'pt' ? 'Total Fontes' : 'Total Sources'}</p>
            <p className="font-bold text-emerald-600">+92M USD</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">{language === 'pt' ? 'Total Usos' : 'Total Uses'}</p>
            <p className="font-bold text-red-600">-86M USD</p>
          </div>
          <div className="bg-blue-50 rounded-lg py-1">
            <p className="text-xs text-slate-500">{language === 'pt' ? 'Folga Financeira' : 'Financial Buffer'}</p>
            <p className="font-bold text-blue-600">+6M USD</p>
          </div>
        </div>
      </div>

      {/* === PERFIL DA DÍVIDA BANCÁRIA === */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-[#0A192F] px-5 py-3">
          <h3 className="font-serif text-base font-bold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {language === 'pt' ? 'Perfil da Dívida Bancária' : 'Banking Debt Profile'}
          </h3>
          <p className="text-slate-300 text-xs mt-0.5">
            {language === 'pt' ? 'Estrutura dos empréstimos que suportam os custos financeiros da empresa' : 'Loan structure supporting company financial costs'}
          </p>
        </div>
        
        <div className="p-5">
          {/* KPIs no topo */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {/* KPI 1: Dívida Bancária Total */}
            <div className="bg-white rounded-xl border border-[#0A192F]/20 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-[#0A192F]/10">
                <Wallet className="w-4 h-4 text-[#0A192F]" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Dívida Bancária Total' : 'Total Bank Debt'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                45.2<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-emerald-100 text-emerald-700">
                  <TrendingDown className="w-3 h-3" />
                  -8% vs 2024
                </span>
              </div>
            </div>

            {/* KPI 2: Taxa Média Ponderada */}
            <div className="bg-white rounded-xl border border-amber-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-amber-100">
                <TrendingUp className="w-4 h-4 text-amber-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Taxa Média Ponderada' : 'Weighted Avg. Rate'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                8.2<span className="text-base text-slate-400 ml-1 font-sans">%</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                  <TrendingUp className="w-3 h-3" />
                  +0.6p.p. vs 2024
                </span>
              </div>
            </div>

            {/* KPI 3: Maior Saldo em Dívida */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-slate-100">
                <Building2 className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Maior Saldo em Dívida' : 'Largest Debt Balance'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                18.5<span className="text-base text-slate-400 ml-1 font-sans">M USD</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-slate-100 text-slate-600">
                  BFA - Linha MLP
                </span>
              </div>
            </div>

            {/* KPI 4: Próxima Maturidade */}
            <div className="bg-white rounded-xl border border-rose-200 p-4 hover:shadow-lg transition-all duration-300 relative overflow-visible">
              <div className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center bg-rose-100">
                <AlertCircle className="w-4 h-4 text-rose-600" />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{language === 'pt' ? 'Próxima Maturidade' : 'Next Maturity'}</p>
              <p className="font-serif text-3xl font-semibold text-primary">
                Mar<span className="text-base text-slate-400 ml-1 font-sans">2026</span>
              </p>
              <div className="flex flex-col gap-1 mt-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded whitespace-nowrap w-fit bg-rose-100 text-rose-700">
                  BAI - 5.8M USD
                </span>
              </div>
            </div>
          </div>

          {/* Tabela Executiva de Empréstimos */}
          <div className="bg-slate-50/50 rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-slate-100 border-b border-slate-200">
              <h4 className="font-semibold text-sm text-slate-700">
                {language === 'pt' ? 'Estrutura de Empréstimos Bancários' : 'Banking Loans Structure'}
              </h4>
              <p className="text-xs text-slate-500 mt-0.5">
                {language === 'pt' ? 'Posição a 31.12.2025' : 'Position as of 31.12.2025'}
              </p>
            </div>
            
            {/* Cabeçalho da Tabela */}
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 text-[10px] font-bold text-slate-600 uppercase tracking-wider border-b border-slate-200">
              <div className="col-span-2">{language === 'pt' ? 'Banco / Instrumento' : 'Bank / Instrument'}</div>
              <div className="col-span-1 text-center">{language === 'pt' ? 'Moeda' : 'Curr.'}</div>
              <div className="col-span-2 text-right">{language === 'pt' ? 'Valor Inicial' : 'Initial Value'}</div>
              <div className="col-span-2 text-right">{language === 'pt' ? 'Saldo Dívida' : 'Debt Balance'}</div>
              <div className="col-span-1 text-center">{language === 'pt' ? 'Taxa' : 'Rate'}</div>
              <div className="col-span-1 text-center">{language === 'pt' ? 'Tipo' : 'Type'}</div>
              <div className="col-span-2 text-center">{language === 'pt' ? 'Maturidade' : 'Maturity'}</div>
              <div className="col-span-1 text-right">{language === 'pt' ? 'Peso' : 'Weight'}</div>
            </div>
            
            {/* Linhas da Tabela */}
            {/* BFA - Linha MLP (Maior saldo - destacado) */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs border-b border-slate-100 bg-amber-50/30 hover:bg-amber-50/50 transition-colors">
              <div className="col-span-2 font-medium text-slate-700 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                BFA - Linha MLP
              </div>
              <div className="col-span-1 text-center text-slate-600">USD</div>
              <div className="col-span-2 text-right text-slate-600">25.0M</div>
              <div className="col-span-2 text-right font-semibold text-slate-800">18.5M</div>
              <div className="col-span-1 text-center text-slate-600">7.5%</div>
              <div className="col-span-1 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 text-blue-700">{language === 'pt' ? 'Fixa' : 'Fixed'}</span></div>
              <div className="col-span-2 text-center text-slate-600">Dez 2028</div>
              <div className="col-span-1 text-right font-semibold text-amber-700">41%</div>
            </div>
            
            {/* BAI - Linha Capital (Próxima maturidade - destacado) */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs border-b border-slate-100 bg-rose-50/30 hover:bg-rose-50/50 transition-colors">
              <div className="col-span-2 font-medium text-slate-700 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-500"></div>
                BAI - Linha Capital
              </div>
              <div className="col-span-1 text-center text-slate-600">USD</div>
              <div className="col-span-2 text-right text-slate-600">8.0M</div>
              <div className="col-span-2 text-right font-semibold text-slate-800">5.8M</div>
              <div className="col-span-1 text-center text-slate-600">8.0%</div>
              <div className="col-span-1 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 text-blue-700">{language === 'pt' ? 'Fixa' : 'Fixed'}</span></div>
              <div className="col-span-2 text-center font-medium text-rose-700">Mar 2026</div>
              <div className="col-span-1 text-right font-semibold text-slate-700">13%</div>
            </div>
            
            {/* BIC - Working Capital */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs border-b border-slate-100 hover:bg-slate-50 transition-colors">
              <div className="col-span-2 font-medium text-slate-700 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                BIC - Working Cap.
              </div>
              <div className="col-span-1 text-center text-slate-600">USD</div>
              <div className="col-span-2 text-right text-slate-600">12.0M</div>
              <div className="col-span-2 text-right font-semibold text-slate-800">9.2M</div>
              <div className="col-span-1 text-center text-slate-600">8.5%</div>
              <div className="col-span-1 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-100 text-amber-700">{language === 'pt' ? 'Var.' : 'Var.'}</span></div>
              <div className="col-span-2 text-center text-slate-600">Jun 2027</div>
              <div className="col-span-1 text-right font-semibold text-slate-700">20%</div>
            </div>
            
            {/* Standard Bank - Project Finance (Maior taxa - destacado) */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs border-b border-slate-100 bg-purple-50/30 hover:bg-purple-50/50 transition-colors">
              <div className="col-span-2 font-medium text-slate-700 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                Std Bank - Proj. Fin.
              </div>
              <div className="col-span-1 text-center text-slate-600">USD</div>
              <div className="col-span-2 text-right text-slate-600">10.0M</div>
              <div className="col-span-2 text-right font-semibold text-slate-800">7.4M</div>
              <div className="col-span-1 text-center font-medium text-purple-700">9.5%</div>
              <div className="col-span-1 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-100 text-amber-700">{language === 'pt' ? 'Var.' : 'Var.'}</span></div>
              <div className="col-span-2 text-center text-slate-600">Set 2027</div>
              <div className="col-span-1 text-right font-semibold text-slate-700">16%</div>
            </div>
            
            {/* BPC - Conta Corrente */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs border-b border-slate-100 hover:bg-slate-50 transition-colors">
              <div className="col-span-2 font-medium text-slate-700 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-slate-400"></div>
                BPC - C/Corrente
              </div>
              <div className="col-span-1 text-center text-slate-600">AOA</div>
              <div className="col-span-2 text-right text-slate-600">5.0M</div>
              <div className="col-span-2 text-right font-semibold text-slate-800">4.3M</div>
              <div className="col-span-1 text-center text-slate-600">7.8%</div>
              <div className="col-span-1 text-center"><span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-100 text-blue-700">{language === 'pt' ? 'Fixa' : 'Fixed'}</span></div>
              <div className="col-span-2 text-center text-slate-600">Dez 2026</div>
              <div className="col-span-1 text-right font-semibold text-slate-700">10%</div>
            </div>
            
            {/* Linha Total */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 text-xs bg-[#0A192F] text-white font-medium">
              <div className="col-span-2 uppercase tracking-wider">Total</div>
              <div className="col-span-1 text-center">-</div>
              <div className="col-span-2 text-right">60.0M</div>
              <div className="col-span-2 text-right font-bold">45.2M</div>
              <div className="col-span-1 text-center">8.2%</div>
              <div className="col-span-1 text-center">-</div>
              <div className="col-span-2 text-center">-</div>
              <div className="col-span-1 text-right font-bold">100%</div>
            </div>
          </div>
          
          {/* Legenda dos destaques */}
          <div className="flex items-center gap-6 mt-4 px-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-amber-50 border border-amber-200"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Maior saldo em dívida' : 'Largest debt balance'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-rose-50 border border-rose-200"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Próxima maturidade crítica' : 'Next critical maturity'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded bg-purple-50 border border-purple-200"></div>
              <span className="text-[10px] text-slate-500">{language === 'pt' ? 'Maior taxa de juro' : 'Highest interest rate'}</span>
            </div>
          </div>
          
          {/* Leitura Executiva */}
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-600 leading-relaxed">
              <span className="font-semibold text-slate-700">{language === 'pt' ? 'Leitura para o Board: ' : 'Board Reading: '}</span>
              {language === 'pt' 
                ? <>A dívida bancária total de <strong>USD 45,2M</strong> está concentrada em 5 linhas de crédito, com destaque para a <strong>Linha MLP do BFA</strong> (41% do total). A taxa média ponderada de <strong>8,2%</strong> subiu face a 2024, refletindo a subida das taxas de referência. A próxima maturidade relevante ocorre em <strong>Março de 2026</strong> (BAI - USD 5,8M), exigindo preparação para refinanciamento ou liquidação. A maior exposição a taxas variáveis está nos financiamentos do BIC e Standard Bank.</>
                : <>Total bank debt of <strong>USD 45.2M</strong> is concentrated in 5 credit lines, with emphasis on <strong>BFA MLP Line</strong> (41% of total). Weighted average rate of <strong>8.2%</strong> rose vs 2024, reflecting reference rate increases. Next relevant maturity is <strong>March 2026</strong> (BAI - USD 5.8M), requiring refinancing or liquidation preparation. Highest variable rate exposure is in BIC and Standard Bank financing.</>}
            </p>
          </div>
        </div>
      </div>


      {/* Two Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Debt Mix - 2025 vs 2024 with CP/MLP split */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-lg font-semibold text-primary">
              {language === 'pt' ? 'Mix de Dívida' : 'Debt Mix'}
            </h3>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-2 py-1 rounded ${parseFloat(debtMixVariation) < 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {debtMixVariation}% vs 2024
              </span>
            </div>
          </div>
          <div className="space-y-4">
            {debtMixData.map((item, idx) => {
              const variation = ((item.value2025 - item.value2024) / item.value2024 * 100).toFixed(0);
              
              return (
                <div key={idx} className="space-y-1.5">
                  {/* Bank Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: bankColors[item.bank] || '#64748B' }}></div>
                      <span className="text-sm font-semibold">{item.bank}</span>
                      <span className="text-xs text-slate-500">{item.rate}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold">{item.value2025}M</span>
                      <span className={`text-xs font-medium ${parseInt(variation) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ({variation > 0 ? '+' : ''}{variation}%)
                      </span>
                    </div>
                  </div>
                  
                  {/* Progress bars - 2025 vs 2024 with tooltip */}
                  {/* Scale: 60M = 100% width */}
                  <div className="space-y-1">
                    {/* 2025 Bar with tooltip */}
                    <div className="flex items-center gap-2 group relative">
                      <span className="text-[10px] text-slate-500 w-8">2025</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden cursor-pointer">
                        {item.hasSplit ? (
                          <div className="flex h-full">
                            <div 
                              className="h-full transition-all duration-500"
                              style={{ 
                                width: `${(item.mlp2025 / 60) * 100}%`, 
                                backgroundColor: bankColors[item.bank] || '#64748B' 
                              }}
                            />
                            <div 
                              className="h-full transition-all duration-500"
                              style={{ 
                                width: `${(item.cp2025 / 60) * 100}%`, 
                                backgroundColor: '#F59E0B'
                              }}
                            />
                          </div>
                        ) : (
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${(item.value2025 / 60) * 100}%`, 
                              backgroundColor: item.type === 'CP' ? '#F59E0B' : (bankColors[item.bank] || '#64748B')
                            }}
                          />
                        )}
                      </div>
                      <span className="text-[10px] text-slate-600 font-medium w-8">{item.pct2025}%</span>
                      
                      {/* Tooltip on hover */}
                      <div className="absolute left-12 bottom-full mb-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="bg-[#0A192F] text-white rounded-lg px-3 py-2 shadow-xl text-xs">
                          <p className="font-semibold text-gold-400 mb-1">{item.bank} - 2025</p>
                          {item.hasSplit ? (
                            <>
                              <div className="flex justify-between py-0.5">
                                <span className="text-slate-300">MLP:</span>
                                <span className="font-semibold">{item.mlp2025}M USD</span>
                              </div>
                              <div className="flex justify-between py-0.5">
                                <span className="text-amber-400">CP:</span>
                                <span className="font-semibold">{item.cp2025}M USD</span>
                              </div>
                              <div className="flex justify-between pt-1 mt-1 border-t border-slate-600">
                                <span className="text-slate-300">Total:</span>
                                <span className="font-bold">{item.value2025}M USD</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between py-0.5">
                              <span className={item.type === 'CP' ? 'text-amber-400' : 'text-slate-300'}>{item.type}:</span>
                              <span className="font-semibold">{item.value2025}M USD</span>
                            </div>
                          )}
                          <div className="absolute -bottom-1 left-4 w-2 h-2 bg-[#0A192F] rotate-45"></div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 2024 Bar (comparison) with tooltip */}
                    <div className="flex items-center gap-2 group relative">
                      <span className="text-[10px] text-slate-400 w-8">2024</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-1.5 overflow-hidden cursor-pointer">
                        {item.hasSplit ? (
                          <div className="flex h-full opacity-50">
                            <div 
                              className="h-full"
                              style={{ 
                                width: `${(item.mlp2024 / 60) * 100}%`, 
                                backgroundColor: bankColors[item.bank] || '#64748B' 
                              }}
                            />
                            <div 
                              className="h-full"
                              style={{ 
                                width: `${(item.cp2024 / 60) * 100}%`, 
                                backgroundColor: '#F59E0B'
                              }}
                            />
                          </div>
                        ) : (
                          <div 
                            className="h-full rounded-full opacity-50"
                            style={{ 
                              width: `${(item.value2024 / 60) * 100}%`, 
                              backgroundColor: item.type === 'CP' ? '#F59E0B' : (bankColors[item.bank] || '#64748B')
                            }}
                          />
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400 w-8">{item.value2024}M</span>
                      
                      {/* Tooltip on hover for 2024 */}
                      <div className="absolute left-12 bottom-full mb-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        <div className="bg-slate-700 text-white rounded-lg px-3 py-2 shadow-xl text-xs">
                          <p className="font-semibold text-slate-300 mb-1">{item.bank} - 2024</p>
                          {item.hasSplit ? (
                            <>
                              <div className="flex justify-between py-0.5">
                                <span className="text-slate-400">MLP:</span>
                                <span className="font-semibold">{item.mlp2024}M USD</span>
                              </div>
                              <div className="flex justify-between py-0.5">
                                <span className="text-amber-400">CP:</span>
                                <span className="font-semibold">{item.cp2024}M USD</span>
                              </div>
                              <div className="flex justify-between pt-1 mt-1 border-t border-slate-500">
                                <span className="text-slate-400">Total:</span>
                                <span className="font-bold">{item.value2024}M USD</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between py-0.5">
                              <span className={item.type === 'CP' ? 'text-amber-400' : 'text-slate-400'}>{item.type}:</span>
                              <span className="font-semibold">{item.value2024}M USD</span>
                            </div>
                          )}
                          <div className="absolute -bottom-1 left-4 w-2 h-2 bg-slate-700 rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Legend - only color meanings */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex justify-between items-center text-xs text-slate-500">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-primary rounded"></div>
                  <span>MLP ({language === 'pt' ? 'Médio/Longo Prazo' : 'Medium/Long Term'})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-amber-500 rounded"></div>
                  <span>CP ({language === 'pt' ? 'Curto Prazo' : 'Short Term'})</span>
                </div>
              </div>
              <div className="text-right">
                <span className="font-semibold text-primary">{totalDebt2025.toFixed(1)}M</span>
                <span className="text-slate-400 ml-1">({totalDebt2024.toFixed(1)}M)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Debt Service - Grouped Bar Chart 2025 vs 2024 */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-serif text-lg font-semibold text-primary mb-4">
            {language === 'pt' ? 'Serviço da Dívida' : 'Debt Service'}
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart 
              data={debtServiceData}
              margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
              barCategoryGap="20%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="bank" tick={{ fontSize: 11, fontWeight: 600 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data2025 = payload.filter(p => p.dataKey.includes('2025'));
                    const data2024 = payload.filter(p => p.dataKey.includes('2024'));
                    const total2025 = data2025.reduce((sum, p) => sum + (p.value || 0), 0);
                    const total2024 = data2024.reduce((sum, p) => sum + (p.value || 0), 0);
                    return (
                      <div className="bg-primary text-white rounded-lg px-4 py-3 shadow-xl">
                        <p className="text-sm font-medium mb-2">{label}</p>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <p className="text-gold-400 font-semibold mb-1">2025</p>
                            <p className="text-slate-300">Amort.: <span className="font-semibold text-white">{data2025.find(p => p.dataKey === 'amortization2025')?.value.toFixed(1)}M</span></p>
                            <p className="text-slate-300">Juros: <span className="font-semibold text-white">{data2025.find(p => p.dataKey === 'interest2025')?.value.toFixed(1)}M</span></p>
                            <p className="font-semibold text-gold-400 mt-1">Total: {total2025.toFixed(1)}M</p>
                          </div>
                          <div>
                            <p className="text-slate-400 font-semibold mb-1">2024</p>
                            <p className="text-slate-400">Amort.: <span className="font-semibold text-slate-300">{data2024.find(p => p.dataKey === 'amortization2024')?.value.toFixed(1)}M</span></p>
                            <p className="text-slate-400">Juros: <span className="font-semibold text-slate-300">{data2024.find(p => p.dataKey === 'interest2024')?.value.toFixed(1)}M</span></p>
                            <p className="font-semibold text-slate-300 mt-1">Total: {total2024.toFixed(1)}M</p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              {/* 2025 Bars */}
              <Bar dataKey="amortization2025" name={language === 'pt' ? 'Amort. 2025' : 'Amort. 2025'} stackId="2025" fill="#0A192F" radius={[0, 0, 0, 0]} />
              <Bar dataKey="interest2025" name={language === 'pt' ? 'Juros 2025' : 'Interest 2025'} stackId="2025" fill="#C5A059" radius={[4, 4, 0, 0]} />
              {/* 2024 Bars */}
              <Bar dataKey="amortization2024" name={language === 'pt' ? 'Amort. 2024' : 'Amort. 2024'} stackId="2024" fill="#64748B" radius={[0, 0, 0, 0]} />
              <Bar dataKey="interest2024" name={language === 'pt' ? 'Juros 2024' : 'Interest 2024'} stackId="2024" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-2 text-xs">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-primary rounded"></div><span>{language === 'pt' ? 'Amort. 2025' : 'Amort. 2025'}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-gold-500 rounded"></div><span>{language === 'pt' ? 'Juros 2025' : 'Interest 2025'}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-slate-500 rounded"></div><span>{language === 'pt' ? 'Amort. 2024' : 'Amort. 2024'}</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-slate-400 rounded"></div><span>{language === 'pt' ? 'Juros 2024' : 'Interest 2024'}</span></div>
          </div>
          {/* Summary - 2025 vs 2024 */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Amort. 2025' : 'Amort. 2025'}</p>
                <p className="font-bold text-primary">{totalAmortization2025.toFixed(1)}M USD</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Juros 2025' : 'Interest 2025'}</p>
                <p className="font-bold text-gold-600">{totalInterest2025.toFixed(1)}M USD</p>
              </div>
              <div className="bg-slate-50 rounded-lg py-1">
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Serviço 2025' : 'Service 2025'}</p>
                <p className="font-bold text-primary">{totalService2025.toFixed(1)}M USD</p>
              </div>
              <div className="bg-emerald-50 rounded-lg py-1">
                <p className="text-xs text-slate-500">vs 2024</p>
                <p className={`font-bold ${parseFloat(serviceVariation) < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {serviceVariation}%
                </p>
                <p className="text-xs text-slate-400">({totalService2024.toFixed(1)}M)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Covenants Section */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-6">
          {language === 'pt' ? 'Covenants Financeiros' : 'Financial Covenants'}
        </h3>
        
        <div className="grid grid-cols-2 gap-6">
          {/* Left Block - Executive Summary with 3 Levels */}
          <div className="space-y-4">
            {/* Level 1: Compact KPIs */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-slate-50 rounded-lg p-2.5">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                  {language === 'pt' ? 'Covenants Monitorizados' : 'Monitored Covenants'}
                </p>
                <p className="font-serif text-xl font-semibold text-primary">4</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-2.5">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                  {language === 'pt' ? 'Em Conformidade' : 'In Compliance'}
                </p>
                <p className="font-serif text-xl font-semibold text-emerald-600">4/4</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2.5">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                  {language === 'pt' ? 'Mais Pressionado' : 'Most Pressured'}
                </p>
                <p className="font-semibold text-amber-700 text-xs">{language === 'pt' ? 'Dívida Líq./EBITDA' : 'Net Debt/EBITDA'}</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-2.5">
                <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                  {language === 'pt' ? 'Folga Mínima' : 'Minimum Headroom'}
                </p>
                <p className="font-serif text-xl font-semibold text-blue-600">0.3x</p>
              </div>
            </div>
            
            {/* Level 2: Executive Statement - Compact */}
            <div className="bg-slate-50/50 rounded-lg px-3 py-2.5 border border-slate-100">
              <p className="text-xs text-slate-600 leading-relaxed">
                {language === 'pt' 
                  ? 'A ABC mantém todos os covenants em conformidade com os limites bancários. O rácio Dívida Líquida/EBITDA apresenta a menor folga (0.3x), sendo monitorizado com atenção redobrada. A posição de liquidez e a trajectória de desalavancagem sustentam a solidez do perfil de crédito.'
                  : 'ABC maintains all covenants in compliance with bank limits. The Net Debt/EBITDA ratio shows the smallest headroom (0.3x), being monitored closely. The liquidity position and deleveraging trajectory support the credit profile strength.'}
              </p>
            </div>
            
            {/* Level 3: Monitoring & Next Tests - Executive Panel */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-5 flex-1">
              {/* Header with Badge */}
              <div className="flex items-center justify-between mb-5">
                <h5 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
                  {language === 'pt' ? 'Monitorização e Próximos Testes' : 'Monitoring & Next Tests'}
                </h5>
                <div className="flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-xs font-semibold">{language === 'pt' ? 'Em conformidade' : 'Compliant'}</span>
                </div>
              </div>
              
              {/* Executive Grid 2x3 */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                {/* Item 1 - Próximo Teste */}
                <div className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">{language === 'pt' ? 'Próximo Teste' : 'Next Test'}</p>
                  <p className="font-serif text-xl font-bold text-primary">{language === 'pt' ? 'T2 2026' : 'Q2 2026'}</p>
                </div>
                
                {/* Item 2 - Mais Pressionado */}
                <div className="bg-white rounded-lg p-3 border border-amber-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">{language === 'pt' ? 'Mais Pressionado' : 'Most Pressured'}</p>
                  <p className="font-semibold text-lg text-amber-600">Emp. 01 | BFA</p>
                </div>
                
                {/* Item 3 - Owner */}
                <div className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">Owner</p>
                  <p className="font-semibold text-lg text-slate-800">CFO / Finance</p>
                </div>
                
                {/* Item 4 - Folga Mínima */}
                <div className="bg-white rounded-lg p-3 border border-blue-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">{language === 'pt' ? 'Folga Mínima' : 'Min. Headroom'}</p>
                  <p className="font-serif text-xl font-bold text-blue-600">0.3x</p>
                </div>
                
                {/* Item 5 - Frequência */}
                <div className="bg-white rounded-lg p-3 border border-slate-100 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">{language === 'pt' ? 'Frequência' : 'Frequency'}</p>
                  <p className="font-semibold text-lg text-slate-800">{language === 'pt' ? 'Mensal' : 'Monthly'}</p>
                </div>
                
                {/* Item 6 - Trigger de Alerta */}
                <div className="bg-white rounded-lg p-3 border border-red-200 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">{language === 'pt' ? 'Trigger de Alerta' : 'Alert Trigger'}</p>
                  <p className="font-semibold text-base text-red-600">Dív.Líq./EBITDA &gt; 2.4x</p>
                </div>
              </div>
              
              {/* Executive Summary Line */}
              <div className="bg-slate-100 rounded-lg px-4 py-3 border-l-4 border-primary">
                <p className="text-sm text-slate-700">
                  {language === 'pt' 
                    ? 'Estado actual: todos os covenants em conformidade, com atenção reforçada ao Empréstimo 01.'
                    : 'Current status: all covenants compliant, with reinforced attention to Loan 01.'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Right Block - Covenants by Loan Instrument */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
              {language === 'pt' ? 'Covenants por Instrumento de Dívida' : 'Covenants by Debt Instrument'}
            </h4>
            
            {/* Loan Covenant Cards */}
            <div className="space-y-5">
              {[
                { 
                  loanNumber: '01',
                  bank: 'BFA',
                  initialAmount: 45.0,
                  currentDebt: 28.5,
                  covenantType: language === 'pt' ? 'Dívida Líquida / EBITDA' : 'Net Debt / EBITDA',
                  current: 2.2, 
                  limit: 2.5, 
                  maxScale: 3.5,
                  headroom: 0.3,
                  unit: 'x',
                  type: 'max'
                },
                { 
                  loanNumber: '02',
                  bank: 'BAI',
                  initialAmount: 30.0,
                  currentDebt: 18.2,
                  covenantType: language === 'pt' ? 'EBITDA / Juros' : 'EBITDA / Interest',
                  current: 8.5, 
                  limit: 4.0, 
                  maxScale: 12,
                  headroom: 4.5,
                  unit: 'x',
                  type: 'min'
                },
                { 
                  loanNumber: '03',
                  bank: 'Standard Bank',
                  initialAmount: 25.0,
                  currentDebt: 15.8,
                  covenantType: language === 'pt' ? 'Dívida Líquida / Capitais Próprios' : 'Net Debt / Equity',
                  current: 0.45, 
                  limit: 0.80, 
                  maxScale: 1.0,
                  headroom: 0.35,
                  unit: 'x',
                  type: 'max'
                },
                { 
                  loanNumber: '04',
                  bank: 'BIC',
                  initialAmount: 20.0,
                  currentDebt: 12.5,
                  covenantType: language === 'pt' ? 'Rácio de Liquidez Geral' : 'Current Ratio',
                  current: 1.8, 
                  limit: 1.2, 
                  maxScale: 2.5,
                  headroom: 0.6,
                  unit: 'x',
                  type: 'min'
                }
              ].map((loan, idx) => {
                const isMaxType = loan.type === 'max';
                const currentPct = (loan.current / loan.maxScale) * 100;
                const limitPct = (loan.limit / loan.maxScale) * 100;
                
                const headroomPct = isMaxType 
                  ? ((loan.limit - loan.current) / loan.limit) * 100
                  : ((loan.current - loan.limit) / loan.limit) * 100;
                
                const statusColor = headroomPct > 30 ? 'text-emerald-600' : headroomPct > 15 ? 'text-amber-600' : 'text-red-600';
                const statusBg = headroomPct > 30 ? 'bg-emerald-500' : headroomPct > 15 ? 'bg-amber-500' : 'bg-red-500';
                
                return (
                  <div key={idx} className="bg-slate-50/50 rounded-lg p-3 border border-slate-100">
                    {/* Line 1 - Loan Title */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">
                        {language === 'pt' ? 'Empréstimo' : 'Loan'} {loan.loanNumber}
                      </span>
                      <span className="text-sm font-semibold text-slate-700">{loan.bank}</span>
                    </div>
                    
                    {/* Line 2 - Loan Details */}
                    <div className="flex items-center gap-4 text-[10px] text-slate-500 mb-2">
                      <span>
                        {language === 'pt' ? 'Valor inicial' : 'Initial'}: <span className="font-semibold text-slate-600">{loan.initialAmount}M USD</span>
                      </span>
                      <span>
                        {language === 'pt' ? 'Dívida actual' : 'Current debt'}: <span className="font-semibold text-slate-600">{loan.currentDebt}M USD</span>
                      </span>
                      <span>
                        {language === 'pt' ? 'Covenant' : 'Covenant'}: <span className="font-semibold text-slate-600">{loan.covenantType}</span>
                      </span>
                    </div>
                    
                    {/* Line 3 - Control Values */}
                    <div className="flex items-center gap-4 text-xs mb-2">
                      <span className="text-slate-500">
                        {language === 'pt' ? 'Actual' : 'Current'}: <span className="font-semibold text-slate-700">{loan.current}{loan.unit}</span>
                      </span>
                      <span className="text-slate-500">
                        {language === 'pt' ? 'Limite' : 'Limit'}: <span className="font-semibold text-slate-700">{loan.limit}{loan.unit}</span>
                      </span>
                      <span className={`font-semibold ${statusColor}`}>
                        {language === 'pt' ? 'Folga' : 'Headroom'}: {loan.headroom}{loan.unit}
                      </span>
                    </div>
                    
                    {/* Line 4 - Bullet Chart */}
                    <div className="relative h-5 bg-slate-200 rounded overflow-hidden">
                      {isMaxType ? (
                        <>
                          <div className="absolute left-0 top-0 h-full bg-emerald-300" style={{ width: '50%' }}></div>
                          <div className="absolute top-0 h-full bg-amber-300" style={{ left: '50%', width: '25%' }}></div>
                          <div className="absolute top-0 h-full bg-red-300" style={{ left: '75%', width: '25%' }}></div>
                        </>
                      ) : (
                        <>
                          <div className="absolute left-0 top-0 h-full bg-red-300" style={{ width: '25%' }}></div>
                          <div className="absolute top-0 h-full bg-amber-300" style={{ left: '25%', width: '25%' }}></div>
                          <div className="absolute top-0 h-full bg-emerald-300" style={{ left: '50%', width: '50%' }}></div>
                        </>
                      )}
                      
                      {/* Limit Line */}
                      <div 
                        className="absolute top-0 h-full w-0.5 bg-slate-700 z-10"
                        style={{ left: `${limitPct}%` }}
                      >
                        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-t-[4px] border-t-slate-700"></div>
                      </div>
                      
                      {/* Current Value Marker */}
                      <div 
                        className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${statusBg} border-2 border-white shadow-sm z-20`}
                        style={{ left: `calc(${currentPct}% - 5px)` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <div className="flex items-center gap-4 text-[10px] text-slate-500">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span>{language === 'pt' ? 'Confortável' : 'Comfortable'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                  <span>{language === 'pt' ? 'Monitorizar' : 'Monitor'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span>{language === 'pt' ? 'Crítico' : 'Critical'}</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-slate-500">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-slate-700"></div>
                  <span>{language === 'pt' ? 'Valor Actual' : 'Current Value'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-0.5 h-3 bg-slate-700"></div>
                  <span>{language === 'pt' ? 'Limite Bancário' : 'Bank Limit'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebtFinancing;
